import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DialogService } from 'primeng/dynamicdialog';
import { Logout } from '../logout/logout';
import { HomeS } from '../../_core/home';
import { Core } from '../../_core/core';
import { BrandingService } from '../../_core/branding.service';
import { AppBuildInfoService } from '../../_core/app-build-info.service';
import { PREVIOUS_VIRTUAL_OFFICE_URL } from '../../welcome-tour/previous-office-url';
import { MessageModule } from 'primeng/message';

interface SubMenuItem {
  label: string;
  route: string;
  icon?: string;
  imgIcon?: string;
  external?: boolean;
  showAttention?: boolean;
  attentionTone?: 'green' | 'orange';
  attentionAriaLabel?: string;
}

interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  open?: boolean;
  show?: boolean;
  showAttention?: boolean;
  attentionTone?: 'green' | 'orange';
  attentionAriaLabel?: string;
  submenu?: SubMenuItem[];
}

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, LucideAngularModule, MessageModule],
  providers: [DialogService],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar {
  private $home = inject(HomeS);
  public homeData = this.$home.homeData;
  private $core = inject(Core);
  private $branding = inject(BrandingService);
  private $dialog = inject(DialogService);
  protected readonly buildInfo = inject(AppBuildInfoService);

  public logoUrl = this.$branding.logoMenu;
  protected readonly isGGM = this.$branding.isGGM;
  protected readonly previousOfficeUrl = PREVIOUS_VIRTUAL_OFFICE_URL;

  public menuItems = computed<MenuItem[]>(() => {
    const selectedBuilding = this.$core.selectedBuilding();
    const currentHomeData = this.homeData();
    const isGGM = this.$branding.isGGM();
    const sendRequestsEnabled = this.$branding.sendRequestsEnabled();
    const termsOfUseUrl = this.$branding.termsOfUseUrl();
    const privacyPolicyUrl = this.$branding.privacyPolicyUrl();
    const aboutUsUrl = this.$branding.aboutUsUrl();

    const onlyBasicMenu = !selectedBuilding;

    const legalLinks: SubMenuItem[] = [];
    if (termsOfUseUrl) {
      legalLinks.push({
        label: 'Términos de uso',
        route: termsOfUseUrl,
        icon: 'video',
        external: true,
      });
    }
    if (privacyPolicyUrl) {
      legalLinks.push({
        label: 'Política de privacidad',
        route: privacyPolicyUrl,
        icon: 'video',
        external: true,
      });
    }
    if (aboutUsUrl) {
      legalLinks.push({ label: 'Acerca de', route: aboutUsUrl, icon: 'video', external: true });
    }

    const hasBilling = currentHomeData?.hasBilling === true;
    const hasGateway = currentHomeData?.hasGateway === true;
    const needsReciboVerdeAttention =
      isGGM && hasBilling && currentHomeData?.hasElectronicSettlement === false;
    const needsElectronicCommunicationsAttention =
      isGGM && currentHomeData?.hasElectronicCommunications === false;

    const settingsAttentionTone = (): 'green' | 'orange' | undefined => {
      if (needsElectronicCommunicationsAttention) {
        return 'orange';
      }
      if (needsReciboVerdeAttention) {
        return 'green';
      }
      return undefined;
    };

    const settingsAttentionAriaLabel = (): string | undefined => {
      if (needsElectronicCommunicationsAttention && needsReciboVerdeAttention) {
        return 'Comunicaciones telemáticas y ReciboVerde pendientes de activar';
      }
      if (needsElectronicCommunicationsAttention) {
        return 'Comunicaciones telemáticas pendientes de activar';
      }
      if (needsReciboVerdeAttention) {
        return 'ReciboVerde pendiente de activar';
      }
      return undefined;
    };

    const basicSettingsSubmenu: SubMenuItem[] = [
      { label: 'Contacto', route: '/contacto', icon: 'phone' },
      ...(isGGM ? [{ label: 'Ayuda', route: '/ayuda', icon: 'help' } satisfies SubMenuItem] : []),
      ...legalLinks,
    ];

    const fullSettingsSubmenu: SubMenuItem[] = [
      {
        label: 'Cuenta y seguridad',
        route: '/configuracion',
        icon: 'help-circle',
      },
      {
        label: 'Datos de abonado',
        route: '/abonado',
        icon: 'help-circle',
        showAttention: needsElectronicCommunicationsAttention,
        attentionTone: 'orange',
        attentionAriaLabel: 'Comunicaciones telemáticas pendientes de activar',
      },
      ...(isGGM && hasBilling
        ? [
            {
              label: 'ReciboVerde',
              route: '/recibo-verde',
              imgIcon: 'assets/img/logo_recibo_verde_sv_solo.svg',
              showAttention: needsReciboVerdeAttention,
              attentionTone: 'green',
              attentionAriaLabel: 'ReciboVerde pendiente de activar',
            } satisfies SubMenuItem,
          ]
        : []),
      { label: 'Contacto', route: '/contacto', icon: 'phone' },
      ...(isGGM ? [{ label: 'Ayuda', route: '/ayuda', icon: 'help' } satisfies SubMenuItem] : []),
      ...legalLinks,
    ];

    const settingsSubmenu = onlyBasicMenu ? basicSettingsSubmenu : fullSettingsSubmenu;

    const menuItems: MenuItem[] = [
      {
        label: 'Inicio',
        route: '/home',
        icon: 'home',
        open: true,
        show: !onlyBasicMenu,
        submenu: [],
      },
      {
        label: 'Mis propiedades',
        route: '/propiedades',
        icon: 'building',
        open: true,
        show: true,
        submenu: [
          { label: 'Lista de propiedades', route: '/propiedades', icon: 'list' },
          { label: 'Añadir propiedad', route: '/propiedades-añadir', icon: 'map-pin' },
        ],
      },
      {
        label: 'Mis liquidaciones',
        route: '/liquidaciones',
        icon: 'file-text',
        show: !onlyBasicMenu && !!currentHomeData?.hasBilling,
        open: false,
        submenu: [],
      },
      {
        label: 'Mis consumos',
        route: '/consumos',
        icon: 'bar-chart-3',
        open: false,
        show: !onlyBasicMenu && !!currentHomeData?.hasBilling,
        submenu: [],
      },
      {
        label: 'Lecturas diarias',
        route: '/lecturas-diarias',
        icon: 'calendar-range',
        show: !onlyBasicMenu && hasGateway,
        submenu: [],
      },
      {
        label: 'Enviar lectura',
        route: '/enviar-lectura',
        icon: 'send',
        show: !onlyBasicMenu && !!currentHomeData?.hasBilling,
        open: false,
        submenu: [],
      },
      {
        label: 'Simula tu liquidación',
        route: '/simulacion-liquidacion',
        icon: 'calculator',
        show:
          isGGM &&
          !onlyBasicMenu &&
          !!currentHomeData?.hasBilling &&
          !!currentHomeData?.suppliers?.some((s) => s.id === 1),
        open: false,
        submenu: [],
      },
      {
        label: 'Alarmas',
        route: '/alarmas',
        icon: 'bell-ring',
        show: !onlyBasicMenu && !!currentHomeData?.hasAlarmModuleAccess,
        open: false,
      },
      {
        label: 'Enviar consulta',
        route: '/enviar-consulta',
        icon: 'messages-square',
        show: !onlyBasicMenu && sendRequestsEnabled,
      },
      {
        label: 'Ajustes',
        route: '/soporte',
        icon: 'user-cog',
        show: true,
        open: false,
        showAttention: needsReciboVerdeAttention || needsElectronicCommunicationsAttention,
        attentionTone: settingsAttentionTone(),
        attentionAriaLabel: settingsAttentionAriaLabel(),
        submenu: settingsSubmenu,
      },
    ];

    return menuItems.filter((item) => item.show !== false);
  });

  private readonly consumptionSectionLabels = [
    'Mis liquidaciones',
    'Mis consumos',
    'Enviar lectura',
    'Simula tu liquidación',
  ] as const;

  private readonly monitoringSectionLabels = ['Lecturas diarias', 'Alarmas'] as const;

  public showConsumptionSection = computed(() =>
    this.menuItems().some((item) =>
      this.consumptionSectionLabels.includes(
        item.label as (typeof this.consumptionSectionLabels)[number],
      ),
    ),
  );

  public showMonitoringSection = computed(() =>
    this.menuItems().some((item) =>
      this.monitoringSectionLabels.includes(
        item.label as (typeof this.monitoringSectionLabels)[number],
      ),
    ),
  );

  public consumptionSectionTitle = computed(() => {
    const data = this.homeData();
    if (data?.hasBilling) {
      return 'Consumos y liquidaciones';
    }
    return '';
  });

  public readonly monitoringSectionTitle = 'Monitorización';

  public toggleSubmenu(item: MenuItem): void {
    item.open = !item.open;
  }

  public logout(): void {
    this.$dialog.open(Logout, {
      closable: false,
    });
  }

  public trackByLabel(index: number, item: MenuItem): string {
    return item.label;
  }

  isSidebarOpen = false;

  public toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }
}
